import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { api } from './api';
import AccessInfoModal, { generatePassword, buildAccessText } from './AccessInfoModal';

import './AdminUsers.css';

// Подсказка рисуется сама, а не средствами браузера: системная всплывающая
// подсказка (title) в админ-панели не появлялась — менялся только курсор.
// Рендер через портал в body, иначе её обрезала бы прокрутка таблицы.
function HoverHint({ hint }) {
  if (!hint) return null;
  return createPortal(
    <span
      className="hover-hint"
      role="tooltip"
      style={{ left: `${hint.x}px`, top: `${hint.y}px` }}
    >
      {hint.text}
    </span>,
    document.body
  );
}

// Дата последнего входа: время приходит в UTC, показываем местное
const lastLoginText = (value) => {
  if (!value) return 'Последний вход: нет данных (пользователь ещё не входил)';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Последний вход: нет данных';
  return `Последний вход: ${date.toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })}`;
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  // Текст с данными для входа, пока открыто окно «Доступ выдан»
  const [accessText, setAccessText] = useState(null);
  const [grantingAccess, setGrantingAccess] = useState(false);
  // Подсказка о последнем входе: текст и точка, над которой её рисуем
  const [hint, setHint] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    middle_name: '',
    password: '',
    role: 'teacher',
    is_active: true,
  });

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    // Фильтрация пользователей по поисковому запросу
    if (!searchQuery.trim()) {
      setFilteredUsers(users);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = users.filter(user => {
        const username = (user.username || '').toLowerCase();
        const email = (user.email || '').toLowerCase();
        const firstName = (user.first_name || '').toLowerCase();
        const lastName = (user.last_name || '').toLowerCase();
        const middleName = (user.middle_name || '').toLowerCase();
        const fullName = `${lastName} ${firstName} ${middleName}`.trim().toLowerCase();
        const role = (user.role || '').toLowerCase();
        
        return username.includes(query) ||
               email.includes(query) ||
               firstName.includes(query) ||
               lastName.includes(query) ||
               middleName.includes(query) ||
               fullName.includes(query) ||
               role.includes(query);
      });
      setFilteredUsers(filtered);
    }
  }, [searchQuery, users]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getUsers();
      // Если данные приходят в формате пагинации, извлекаем results
      const users = Array.isArray(data) ? data : (data.results || data);
      setUsers(users);
      setFilteredUsers(users);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      if (editingUser) {
        await api.updateUser(editingUser.id, formData);
      } else {
        await api.createUser(formData);
      }
      setShowForm(false);
      setEditingUser(null);
      setFormData({
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        middle_name: '',
        password: '',
        role: 'teacher',
        is_active: true,
      });
      loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username || '',
      email: user.email || '',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      middle_name: user.middle_name || '',
      password: '',
      role: user.role || 'teacher',
      is_active: user.is_active !== false,
    });
    setShowForm(true);
  };

  // Старый пароль узнать нельзя — сервер хранит только хэш, — поэтому
  // выдача доступа всегда задаёт новый. Если администратор уже ввёл пароль
  // в форме, берём его, иначе придумываем случайный.
  const handleGrantAccess = async () => {
    if (!editingUser || grantingAccess) return;
    const typedPassword = formData.password;
    if (typedPassword && typedPassword.length < 8) {
      setError('Пароль должен содержать минимум 8 символов');
      return;
    }
    const fullName = [editingUser.last_name, editingUser.first_name, editingUser.middle_name]
      .filter(Boolean).join(' ') || editingUser.full_name || editingUser.username;
    const confirmed = window.confirm(
      `Пользователю «${fullName}» будет задан новый пароль. `
      + 'Старый пароль перестанет работать. Продолжить?'
    );
    if (!confirmed) return;

    const password = typedPassword || generatePassword();
    setError(null);
    setGrantingAccess(true);
    try {
      // Отправляем только пароль: несохранённые правки остальных полей
      // не должны уходить на сервер без нажатия «Сохранить»
      await api.updateUser(editingUser.id, { password });
      setFormData((prev) => ({ ...prev, password: '' }));
      setAccessText(buildAccessText({
        fullName,
        // Логин — сохранённый, а не из формы: входить придётся по нему
        username: editingUser.username,
        password,
        loginUrl: `${window.location.origin}/`,
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setGrantingAccess(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Вы уверены, что хотите удалить этого пользователя?')) {
      return;
    }

    try {
      await api.deleteUser(id);
      loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      first_name: '',
      last_name: '',
      password: '',
      role: 'teacher',
      is_active: true,
    });
  };

  if (loading) {
    return <div className="admin-loading">Загрузка пользователей...</div>;
  }

  const showHint = (event, text) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setHint({ text, x: rect.left + rect.width / 2, y: rect.top });
  };

  return (
    <div className="admin-users">
      <HoverHint hint={hint} />

      {accessText && (
        <AccessInfoModal text={accessText} onClose={() => setAccessText(null)} />
      )}

      <div className="admin-toolbar">
        <div className="admin-toolbar-actions">
          <div className="admin-search">
            <svg className="admin-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <input
              type="text"
              placeholder="Поиск пользователей..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="admin-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="admin-search-clear"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </div>
          <button className="admin-add-button" onClick={() => setShowForm(true)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Добавить пользователя
          </button>
        </div>
      </div>

      {error && (
        <div className="admin-error">
          {error}
        </div>
      )}

      {showForm && (
        <div className="admin-form-overlay">
          <form className="admin-form" onSubmit={handleSubmit}>
            <div className="form-header">
              <h4>{editingUser ? 'Редактировать пользователя' : 'Создать пользователя'}</h4>
              <button type="button" className="form-close" onClick={handleCancel}>×</button>
            </div>

            <div className="form-group">
              <label>Имя пользователя *</label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Имя</label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>Фамилия</label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Отчество</label>
              <input
                type="text"
                name="middle_name"
                value={formData.middle_name}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label>{editingUser ? 'Новый пароль (оставьте пустым, чтобы не менять)' : 'Пароль *'}</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required={!editingUser}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Роль</label>
                <select name="role" value={formData.role} onChange={handleInputChange}>
                  <option value="teacher">Учитель</option>
                  <option value="admin">Администратор</option>
                </select>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                  />
                  Активен
                </label>
              </div>
            </div>

            <div className="form-actions">
              {editingUser && (
                <button
                  type="button"
                  className="admin-button admin-button-access"
                  onClick={handleGrantAccess}
                  disabled={grantingAccess}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <circle cx="7.5" cy="15.5" r="4.5"></circle>
                    <path d="M10.7 12.3 21 2"></path>
                    <path d="m16 7 3 3"></path>
                    <path d="m19 4 2 2"></path>
                  </svg>
                  {grantingAccess ? 'Выдаём доступ...' : 'Выдать доступ'}
                </button>
              )}
              <button type="button" className="admin-button admin-button-cancel" onClick={handleCancel}>
                Отмена
              </button>
              <button type="submit" className="admin-button admin-button-save">
                {editingUser ? 'Сохранить' : 'Создать'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Имя пользователя</th>
              <th>ФИО</th>
              <th>Email</th>
              <th>Роль</th>
              <th>Статус</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="7" className="admin-empty">Нет пользователей</td>
              </tr>
            ) : (
              filteredUsers.map(user => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>
                    <span
                      className="user-login"
                      tabIndex={0}
                      aria-label={`${user.username}. ${lastLoginText(user.last_login)}`}
                      onMouseEnter={(e) => showHint(e, lastLoginText(user.last_login))}
                      onMouseLeave={() => setHint(null)}
                      onFocus={(e) => showHint(e, lastLoginText(user.last_login))}
                      onBlur={() => setHint(null)}
                    >
                      {user.username}
                    </span>
                  </td>
                  <td>{user.full_name || (user.last_name && user.first_name ? `${user.last_name} ${user.first_name} ${user.middle_name || ''}`.trim() : '-')}</td>
                  <td>{user.email || '-'}</td>
                  <td>
                    <span className={`role-badge ${user.role}`}>
                      {user.role === 'admin' ? 'Администратор' : 'Учитель'}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                      {user.is_active ? 'Активен' : 'Неактивен'}
                    </span>
                  </td>
                  <td>
                    <div className="admin-actions">
                      <button
                        className="admin-action-button admin-action-edit"
                        onClick={() => handleEdit(user)}
                        title="Редактировать"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                      </button>
                      <button
                        className="admin-action-button admin-action-delete"
                        onClick={() => handleDelete(user.id)}
                        title="Удалить"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

